// Global Instructions Rule Applied!

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { QUARTER_DATE_FORMAT as DATE_FORMAT, lastQuarterEnd, oneYearPriorQuarterEnd, quarterEndOptions } from '../../../core/utils/quarterEnds.ts'
import { Progress } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'

import { FormSection } from '../../../core/components/FormSection.tsx'
import Button from '../../../core/components/Button.jsx'
import { InputManager } from '../../../core/components/InputManager/index.ts'
import { FieldTypeEnum } from '../../../core/components/InputManager/types.ts'
import type { FieldConfig, FieldValue } from '../../../core/components/InputManager/types.ts'
import ViewToolbar from '../../../core/components/ViewToolbar/index.tsx'
import DownloadPdfToolbarButton from '../../../core/components/ViewToolbar/parts/DownloadPdfToolbarButton.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import { MENU_ITEM_KEY_REPORTS } from '../../../state_manager/globals.ts'
import type { RootState } from '../../../state_manager/store.ts'
import type { FundsModel } from '../../../state_manager/data_models/funds_model.ts'
import type { FundGroupConfigModel } from '../../../state_manager/data_models/fund_group_config_model.ts'
import type { InvestorParentConfigModel } from '../../../state_manager/data_models/investor_parent_config_model.ts'
import type {
  DocumentReportModel,
  DocumentReportParameterModel
} from '../../../state_manager/data_models/document_report_model.ts'
import { useDocumentReportPreview } from '../hooks/useDocumentReportPreview.ts'
import { useDocumentReportDownload } from '../hooks/useDocumentReportDownload.ts'
import type { BatchTarget } from '../hooks/useDocumentReportDownload.ts'
import { ReportPreviewPane } from './ReportPreviewPane.tsx'
import { CustomizeReportDrawer } from './CustomizeReportDrawer.tsx'
import { HighlightsReviewStatus } from './HighlightsReviewStatus.tsx'
import { DescriptionsReviewStatus } from './DescriptionsReviewStatus.tsx'

const EMPTY_REPORTS: DocumentReportModel[] = []
const EMPTY_FUNDS: FundsModel[] = []
const EMPTY_FUND_GROUPS: FundGroupConfigModel[] = []
const EMPTY_INVESTOR_PARENTS: InvestorParentConfigModel[] = []

// Token a cfg default_value may carry; resolved at render time rather than
// baked into the seed so the default keeps moving with the calendar.
const LAST_QUARTER_END_TOKEN = '{last_quarter_end}'
// Same idea, four quarters earlier — the natural start for a one-year
// period-range report.
const ONE_YEAR_PRIOR_QUARTER_END_TOKEN = '{one_year_prior_quarter_end}'

// In-app modules a document report may deep-link to. A rendered document is
// data, not code we wrote in this repo — the whitelist keeps a compromised or
// buggy document from navigating anywhere else.
const APP_LINK_MODULES = new Set(['companies', 'funds', 'consolidated-funds', 'deals', 'rounds'])
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type ParamValues = Record<string, FieldValue>

// Quarter helpers live in core/utils/quarterEnds.ts (shared with Period
// Highlights so the two pickers cannot drift).

/**
 * Whether a parameter's control should be shown.
 *
 * cfg lets a parameter depend on a sibling's value (the period pair: a quarter
 * picker OR a free date, never both). A hidden control is also exempt from the
 * required check — the user cannot satisfy something they cannot see.
 */
function isVisible(parameter: DocumentReportParameterModel, values: ParamValues): boolean {
  if (!parameter.visible_when_param) return true
  return String(values[parameter.visible_when_param] ?? '') === String(parameter.visible_when_value ?? '')
}

function resolveDefault(parameter: DocumentReportParameterModel): FieldValue {
  if (parameter.default_value === LAST_QUARTER_END_TOKEN) return lastQuarterEnd()
  if (parameter.default_value === ONE_YEAR_PRIOR_QUARTER_END_TOKEN) return oneYearPriorQuarterEnd()
  if (parameter.default_value != null) return parameter.default_value
  if (parameter.input_type === 'checkbox') return false
  return parameter.is_multiple ? [] : undefined
}

function defaultsFor(report: DocumentReportModel | undefined): ParamValues {
  if (!report) return {}
  return Object.fromEntries(report.parameters.map((p) => [p.param_key, resolveDefault(p)]))
}

/**
 * Bring restored values into line with each parameter's current shape.
 *
 * Saved preferences outlive cfg changes: a preference stored while Fund was a
 * single select holds a bare id, and handing that to a multi-select renders a
 * string where a list is expected. Coerced in both directions so a parameter
 * can gain or lose is_multiple without stranding everyone who used it before.
 */
function normalizeParams(report: DocumentReportModel | undefined, values: ParamValues): ParamValues {
  if (!report) return values
  const normalized: ParamValues = { ...values }
  for (const parameter of report.parameters) {
    const value = normalized[parameter.param_key]
    if (value === undefined || value === null) continue
    if (parameter.is_multiple && !Array.isArray(value)) {
      normalized[parameter.param_key] = value === '' ? [] : [value]
    } else if (!parameter.is_multiple && Array.isArray(value)) {
      normalized[parameter.param_key] = value[0]
    }
  }
  return normalized
}

type ParameterFieldType =
  | FieldTypeEnum.SELECT
  | FieldTypeEnum.DATE
  | FieldTypeEnum.CHECKBOX
  | FieldTypeEnum.NUMBER
  | FieldTypeEnum.TEXT

function fieldTypeFor(inputType: DocumentReportParameterModel['input_type']): ParameterFieldType {
  switch (inputType) {
    case 'select':
      return FieldTypeEnum.SELECT
    case 'date':
      return FieldTypeEnum.DATE
    case 'checkbox':
      return FieldTypeEnum.CHECKBOX
    case 'number':
      return FieldTypeEnum.NUMBER
    default:
      return FieldTypeEnum.TEXT
  }
}

/** Values selected for the report's fan-out parameter, as a list. */
function batchValues(report: DocumentReportModel | undefined, values: ParamValues): string[] {
  const key = report?.batch_param_key
  if (!key) return []
  const selected = values[key]
  if (Array.isArray(selected)) return selected.map(String).filter(Boolean)
  return selected === undefined || selected === null || selected === '' ? [] : [String(selected)]
}

/**
 * Params for ONE report.
 *
 * The fan-out parameter is a list on the screen but always a single value to
 * the builder — the report itself is per-fund, and it is the screen that
 * repeats it. Used for the preview (which shows the first selection) and for
 * each pass of a download.
 */
function paramsForValue(
  report: DocumentReportModel | undefined,
  values: ParamValues,
  value: string | undefined
): ParamValues {
  const key = report?.batch_param_key
  if (!key) return values
  return { ...values, [key]: value ?? null }
}

function missingRequired(report: DocumentReportModel | undefined, values: ParamValues): string[] {
  if (!report) return []
  return report.parameters
    .filter((p) => {
      if (!p.is_required || !isVisible(p, values)) return false
      const value = values[p.param_key]
      return value === undefined || value === null || value === '' ||
        (Array.isArray(value) && value.length === 0)
    })
    .map((p) => p.label)
}

export function DocumentReportsForm() {
  const { menuItemId, preferenceRow, persistPreference } =
    useMenuItemUserPreference(MENU_ITEM_KEY_REPORTS)
  const { openFormInTab } = useTabManagerContext()

  const documentReports = useSelector(
    (state: RootState) => (state.globalConfigs?.document_reports ?? EMPTY_REPORTS) as DocumentReportModel[]
  )
  const funds = useSelector((state: RootState) => state.globalConfigs?.funds ?? EMPTY_FUNDS)
  const fundGroups = useSelector((state: RootState) => state.globalConfigs?.fund_groups ?? EMPTY_FUND_GROUPS)
  const investorParents = useSelector(
    (state: RootState) => state.globalConfigs?.investor_parents ?? EMPTY_INVESTOR_PARENTS
  )

  // Reports registered against this menu item, in cfg order.
  const reports = useMemo(
    () =>
      documentReports
        .filter((r) => r.is_active && (!menuItemId || r.menu_item_id === menuItemId))
        .slice()
        .sort((a, b) => a.sequence - b.sequence || a.name.localeCompare(b.name)),
    [documentReports, menuItemId]
  )

  const preference = (preferenceRow?.data ?? {}) as Record<string, unknown>
  const preferredCode = typeof preference.document_report_code === 'string'
    ? preference.document_report_code
    : undefined

  const [reportCode, setReportCode] = useState<string>('')
  const [draft, setDraft] = useState<ParamValues>({})
  const [applied, setApplied] = useState<{ code: string; params: ParamValues } | null>(null)

  const { blobUrl, pageCount, highlights, unreviewedDescriptions, loading, error, refresh, clear } =
    useDocumentReportPreview()
  const { progress, run: runDownload, cancel: cancelDownload } = useDocumentReportDownload()

  const selectedReport = useMemo(
    () => reports.find((r) => r.code === reportCode),
    [reports, reportCode]
  )

  // Seed the selection once cfg has landed: the user's last choice if it still
  // exists, else the first report.
  useEffect(() => {
    if (reportCode || reports.length === 0) return
    const initial = reports.find((r) => r.code === preferredCode) ?? reports[0]
    setReportCode(initial.code)
    const savedParams = (preference.params ?? {}) as ParamValues
    setDraft(
      normalizeParams(initial, {
        ...defaultsFor(initial),
        ...(initial.code === preferredCode ? savedParams : {})
      })
    )
  }, [reports, reportCode, preferredCode, preference.params])

  const handleReportChange = useCallback(
    (code: string) => {
      setReportCode(code)
      setDraft(defaultsFor(reports.find((r) => r.code === code)))
      setApplied(null)
      clear()
    },
    [reports, clear]
  )

  const handleFieldChange = useCallback(
    (key: string, value: FieldValue) => {
      if (key === 'report_code') {
        handleReportChange(String(value))
        return
      }
      setDraft((prev) => ({ ...prev, [key]: value }))
    },
    [handleReportChange]
  )

  const optionsFor = useCallback(
    (parameter: DocumentReportParameterModel) => {
      if (parameter.options_source === 'funds') {
        return funds
          .filter((f) => f.managed_by_us)
          .map((f) => ({ value: f.id, label: f.name }))
      }
      if (parameter.options_source === 'fund_groups') {
        return fundGroups.map((g) => ({ value: g.id, label: g.name }))
      }
      // Funds and consolidated funds in one picker. A consolidated fund is not
      // a fund and must never appear in an operational fund selector — you
      // cannot buy a security in one — but it is a legitimate thing to report
      // on, so reporting parameters may offer both. The two id spaces are
      // distinct; the backend resolves which kind was chosen.
      if (parameter.options_source === 'funds_and_fund_groups') {
        return [
          ...funds.filter((f) => f.managed_by_us).map((f) => ({ value: f.id, label: f.name })),
          ...fundGroups.map((g) => ({ value: g.id, label: g.name }))
        ]
      }
      if (parameter.options_source === 'quarter_ends') {
        // Generated rather than stored so the list keeps up with the calendar.
        return quarterEndOptions()
      }
      // Investor parents with at least one closed commitment — the Investor
      // Performance Report's target picker.
      if (parameter.options_source === 'investor_parents') {
        return investorParents.map((p) => ({ value: p.id, label: p.name }))
      }
      return parameter.options_json
    },
    [funds, fundGroups, investorParents]
  )

  // The report picker plus one control per cfg parameter row.
  const fields: FieldConfig[] = useMemo(() => {
    const reportField: FieldConfig = {
      key: 'report_code',
      label: 'Report',
      type: FieldTypeEnum.SELECT,
      value: reportCode,
      options: reports.map((r) => ({ value: r.code, label: r.name })),
      editable: true
    }

    const parameterFields = (selectedReport?.parameters ?? [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .filter((parameter) => isVisible(parameter, draft))
      .map((parameter) => {
        const base: FieldConfig = {
          key: parameter.param_key,
          label: parameter.label,
          type: fieldTypeFor(parameter.input_type),
          value: draft[parameter.param_key],
          editable: true
        }
        if (parameter.input_type === 'select') {
          return {
            ...base,
            options: optionsFor(parameter),
            ...(parameter.is_multiple ? { mode: 'multiple' as const, tagDisplay: true } : {}),
            placeholder: `Select ${parameter.label.toLowerCase()}`
          }
        }
        if (parameter.input_type === 'date') {
          return { ...base, format: DATE_FORMAT }
        }
        if (parameter.input_type === 'checkbox') {
          return { ...base, checkboxLabel: parameter.label, label: '', labelPosition: 'none' as const }
        }
        return base
      })

    return [reportField, ...parameterFields]
  }, [reportCode, reports, selectedReport, draft, optionsFor])

  const dirty = useMemo(() => {
    if (!applied) return Boolean(selectedReport)
    if (applied.code !== reportCode) return true
    return JSON.stringify(applied.params) !== JSON.stringify(draft)
  }, [applied, reportCode, draft, selectedReport])

  const handleApply = useCallback(async () => {
    if (!selectedReport) return
    const missing = missingRequired(selectedReport, draft)
    if (missing.length > 0) {
      showErrorToast(`${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required`)
      return
    }
    const params = { ...draft }
    setApplied({ code: selectedReport.code, params })
    // The preview is one document, so it shows the first selection; Download
    // is what produces the rest. Which one is on screen is named in the
    // preview section's title so this is never a guess.
    const first = batchValues(selectedReport, params)[0]
    await refresh(selectedReport.code, paramsForValue(selectedReport, params, first))
    void persistPreference(undefined, { document_report_code: selectedReport.code, params })
  }, [selectedReport, draft, refresh, persistPreference])

  const handleCancel = useCallback(() => {
    if (applied) {
      setReportCode(applied.code)
      setDraft({ ...applied.params })
    } else {
      setDraft(defaultsFor(selectedReport))
    }
  }, [applied, selectedReport])

  const handleReset = useCallback(() => {
    setDraft(defaultsFor(selectedReport))
  }, [selectedReport])

  // Selected fan-out values paired with the labels shown in the picker, so
  // progress and any failure can name the fund rather than its id.
  const appliedTargets: BatchTarget[] = useMemo(() => {
    if (!applied || !selectedReport?.batch_param_key) return []
    const parameter = selectedReport.parameters.find(
      (p) => p.param_key === selectedReport.batch_param_key
    )
    const options = parameter ? optionsFor(parameter) : []
    const labelFor = new Map(
      (options ?? []).map((o) => [String(o.value), String(o.label ?? o.value)])
    )
    return batchValues(selectedReport, applied.params).map((value) => ({
      value,
      label: labelFor.get(value) ?? value
    }))
  }, [applied, selectedReport, optionsFor])

  const handleDownload = useCallback(async () => {
    if (!applied || !selectedReport) return
    const result = await runDownload({
      reportCode: applied.code,
      params: applied.params,
      batchParamKey: selectedReport.batch_param_key,
      // No fan-out parameter means a single pass with the params as they are.
      targets: appliedTargets.length > 0
        ? appliedTargets
        : [{ value: '', label: selectedReport.name }]
    })

    if (!result.success) {
      showErrorToast(result.error || 'Download failed')
      return
    }
    if (result.failed?.length) {
      // Partial success is still a download — say what is missing from it
      // rather than letting a short archive pass for a complete one.
      toast.warning(
        `${result.filename} downloaded, but ${result.failed.length} failed: ${result.failed.join(', ')}`
      )
      return
    }
    toast.success(`${result.filename} downloaded`)
  }, [applied, selectedReport, appliedTargets, runDownload])

  // Geometry of the report the preview is SHOWING (the applied one, not the
  // draft selection) — switching the picker must not rescale a stale preview.
  const appliedReport = useMemo(
    () => reports.find((r) => r.code === applied?.code),
    [reports, applied]
  )

  // The single-run params the preview was rendered with — what the Customize
  // drawer's context and saves must key on (customizations are per applied
  // run, so this stays pinned to the applied values, not the draft).
  const previewParams = useMemo(() => {
    if (!applied) return {}
    const first = batchValues(appliedReport, applied.params)[0]
    return paramsForValue(appliedReport, applied.params, first)
  }, [applied, appliedReport])

  const [customizeOpen, setCustomizeOpen] = useState(false)

  // After a save the stored edits are part of the rendered document, so the
  // preview refetches to show them.
  const handleCustomizeSaved = useCallback(() => {
    if (!applied) return
    void refresh(applied.code, previewParams)
  }, [applied, previewParams, refresh])

  // A document's in-app entity links arrive as postMessages from the sandboxed
  // preview frame (the pane has already verified the source is its iframe).
  // Validate hard before navigating: whitelist the module and require a UUID —
  // the document is rendered from data, and data does not get to choose
  // arbitrary navigation targets.
  const handleAppLink = useCallback(
    (payload: { module: string; id: string; label: string }) => {
      if (!APP_LINK_MODULES.has(payload.module) || !UUID_PATTERN.test(payload.id)) return
      openFormInTab(payload.module, payload.id, payload.label || null)
    },
    [openFormInTab]
  )

  const previewTitle = useMemo(() => {
    const pages = pageCount ? ` — ${pageCount} page${pageCount === 1 ? '' : 's'}` : ''
    if (appliedTargets.length > 1) {
      return `Preview: ${appliedTargets[0].label}${pages} · Download produces all ${appliedTargets.length}`
    }
    return `Preview${pages}`
  }, [pageCount, appliedTargets])

  return (
    <div className='flex flex-1 min-h-0 flex-col overflow-hidden'>
      <ViewToolbar
        moduleKey='reports'
        searchEnabled={false}
        addButtonEnabled={false}
        activeFilters={[]}
        onRefreshData={() => void handleApply()}
        beforeAddSlot={
          <DownloadPdfToolbarButton
            onClick={handleDownload}
            loading={progress.running}
            disabled={!applied || loading || Boolean(error)}
            label={appliedTargets.length > 1 ? `Download ${appliedTargets.length} PDFs` : undefined}
          />
        }
      />

      <FormSection title='Reports' scrollable={false} className='flex-shrink-0'>
        <div className='max-w-2xl mt-1'>
          <InputManager
            key={`document-reports-${reportCode}`}
            inputMode='edit'
            fields={fields}
            onChange={handleFieldChange}
          />
          <div className='flex flex-wrap items-center justify-between gap-2 pt-1.5 mt-3 border-t border-gray-200'>
            {/*
              A multi-fund run is a minute or more of work, so it reports which
              report it is on rather than sitting behind a spinner. Cancel stops
              at the next report — the one in flight is already rendering on the
              server and abandoning it mid-way would leave that work orphaned.
            */}
            {progress.running ? (
              <div className='flex items-center gap-3 min-w-0'>
                <Progress
                  type='line'
                  percent={Math.round((progress.completed / Math.max(progress.total, 1)) * 100)}
                  size='small'
                  className='!mb-0 !w-40'
                />
                <span className='text-xs text-gray-600 truncate'>
                  Generating {Math.min(progress.completed + 1, progress.total)} of {progress.total}
                  {progress.currentLabel ? ` — ${progress.currentLabel}` : ''}
                </span>
                <Button variant='cancel' size='small' onClick={cancelDownload}>
                  Cancel
                </Button>
              </div>
            ) : (
              <span />
            )}

            <div className='flex flex-wrap items-center gap-2'>
              {appliedReport?.supports_customization && (
                <Button
                  variant='edit'
                  size='small'
                  // Enabled only once a run exists: the drawer edits THIS
                  // run's generated content, so there must be one to edit.
                  disabled={!applied || loading || Boolean(error)}
                  onClick={() => setCustomizeOpen(true)}
                >
                  Customize
                </Button>
              )}
              <Button variant='edit' size='small' onClick={handleReset}>
                Reset
              </Button>
              {dirty && (
                <>
                  <Button variant='cancel' size='small' onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    variant='save'
                    size='small'
                    onClick={() => {
                      void handleApply()
                    }}
                  >
                    Apply
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection
        title={previewTitle}
        scrollable={false}
        className='flex-1 min-h-0 flex flex-col overflow-hidden mt-2'
        // Whether the period highlights in THIS run were reviewed and signed
        // off — clear to any user on every run (owner, 2026-09-07).
        // So are the agent-written company descriptions it prints (2026-11-03).
        titleSuffix={applied && !loading && !error ? (
          <span className='flex items-center gap-1'>
            {highlights ? <HighlightsReviewStatus status={highlights} /> : null}
            <DescriptionsReviewStatus unreviewed={unreviewedDescriptions} />
          </span>
        ) : null}
        headerActions={applied && !loading && !error && highlights?.set_id ? (
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-xs'
            onClick={() => openFormInTab('period-highlights', highlights.set_id as string, `${highlights.scope_name ?? 'Highlights'} · ${highlights.period_label ?? ''}`)}
          >
            {highlights.status === 'signed_off' ? 'Open the highlight set' : 'Review and sign off'}
          </button>
        ) : null}
      >
        <div className='flex-1 min-h-0'>
          <ReportPreviewPane
            blobUrl={blobUrl}
            pageCount={pageCount}
            loading={loading}
            error={error}
            pageSize={appliedReport?.page_size}
            orientation={appliedReport?.orientation}
            onAppLink={handleAppLink}
          />
        </div>
      </FormSection>

      {appliedReport?.supports_customization && applied && (
        <CustomizeReportDrawer
          open={customizeOpen}
          onClose={() => setCustomizeOpen(false)}
          reportCode={applied.code}
          params={previewParams}
          onSaved={handleCustomizeSaved}
        />
      )}
    </div>
  )
}
